import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { queryKeys } from '../../../lib/queryKeys';
import { useCalendarGridQuery } from '../../../hooks/useCalendarGrid';
import { useCalendarWeekQuery } from '../../../hooks/useCalendarWeek';
import { useBusinessDetailsQuery } from '../../../hooks/useBusinessDetails';
import CalendarGrid from './components/CalendarGrid';
import WeekView from './components/WeekView';
import CalendarSkeleton from './components/CalendarSkeleton';
import StaffContextMenu from './components/StaffContextMenu';
import QuickAbsenceModal from './components/QuickAbsenceModal';
import StaffStatusModal from './components/StaffStatusModal';
import QuickBookLauncher from '../../../components/QuickBookModal/QuickBookLauncher';
import QuickBookModal from '../../../components/QuickBookModal/QuickBookModal';
import { useToast } from '../../../contexts/ToastContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import type { StaffAbsenceDto } from '../../../types/api-responses';
import DateField from '../../../components/DateField/DateField';
import s from './CalendarPage.module.css';

/** O3 (SO-20) — calendar dates in the owner's language, day-first (never en-US). */
const dateLocale = (language: string) => (language === 'ar' ? 'ar' : 'en-GB');

const LAST_BRANCH_KEY = 'calendarLastBranchId';
const LAST_VIEW_KEY = 'calendarLastView';
type CalendarView = 'day' | 'week';

/** Sunday-start week: Sun=0..Sat=6. Returns yyyy-MM-dd of the Sunday on/before `iso`. */
function weekStartIso(iso: string): string {
    const d = new Date(`${iso}T00:00:00`);
    const dow = d.getDay(); // 0=Sun
    d.setDate(d.getDate() - dow);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** "Sun 17 – Sat 23 May" — mockup §B header label. */
function formatWeekRange(startIso: string, language: string): string {
    const d0 = new Date(`${startIso}T00:00:00`);
    const d6 = new Date(d0);
    d6.setDate(d0.getDate() + 6);
    const fmt = (dd: Date) =>
        dd.toLocaleDateString(dateLocale(language), { weekday: 'short', day: 'numeric' });
    const monthOf = (dd: Date) =>
        dd.toLocaleDateString(dateLocale(language), { month: 'short' });
    return d0.getMonth() === d6.getMonth()
        ? `${fmt(d0)} – ${fmt(d6)} ${monthOf(d6)}`
        : `${fmt(d0)} ${monthOf(d0)} – ${fmt(d6)} ${monthOf(d6)}`;
}

/** Local-date string in yyyy-MM-dd (browser TZ — matches what the date input emits). */
function localTodayIso(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function addDaysIso(iso: string, days: number): string {
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + days);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** "Thu, May 21, 2026" — long form for empty/closed placards. */
function formatDateLong(iso: string, language: string): string {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(dateLocale(language), {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

/** "Wed 20 May" — short form for the date-nav, matches mockup §A. */
function formatDateShort(iso: string, language: string): string {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(dateLocale(language), {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    });
}

/**
 * AB#1749 — Owner calendar day-view (C3 of Calendar v1 Epic AB#1746).
 *
 * Read-only surface. Branch picker (hidden when single-branch), date
 * picker with prev/today/next, Day-view tab (active) + Week-view tab
 * (stub disabled). Body renders the grid via `<CalendarGrid />`.
 *
 * Booking creation, status mutation, and quick-book modals are
 * deferred to sibling PRs C5 / C6 — `AppointmentPeekPopover` shows a
 * placeholder noting "Full booking flow coming in C5" when a card is
 * clicked.
 */
const CalendarPage: React.FC = () => {
    const { showToast } = useToast();
    const { t, language } = useLanguage();
    const queryClient = useQueryClient();
    // CR-2026-05-30-v3 — when navigating from /appointments list row, we land
    // here as /calendar?day=YYYY-MM-DD&branchId=N&apptId=X&from=list. Honor
    // those params so the list-to-calendar deep link doesn't drop the user on
    // the wrong branch (the historical default was localStorage last-choice,
    // which made the appointment invisible if the booking was at a different
    // branch from the last one viewed).
    const [searchParams, setSearchParams] = useSearchParams();
    const urlDay = searchParams.get('day');
    const urlBranchId = (() => {
        const raw = searchParams.get('branchId');
        const n = raw ? Number(raw) : NaN;
        return Number.isFinite(n) && n > 0 ? n : null;
    })();
    const urlApptId = (() => {
        const raw = searchParams.get('apptId');
        const n = raw ? Number(raw) : NaN;
        return Number.isFinite(n) && n > 0 ? n : null;
    })();

    const [date, setDate] = useState<string>(() => urlDay && /^\d{4}-\d{2}-\d{2}$/.test(urlDay) ? urlDay : localTodayIso());
    const [branchId, setBranchId] = useState<number | null>(null);
    // Track whether the URL deep-link has already been consumed, so we don't
    // re-override the branch every time the user clicks the picker.
    const urlBranchConsumedRef = useRef<boolean>(false);
    // Stash the apptId so the renderer can pulse the matching card briefly
    // (matches Dina §2.6 — "auto-scroll into view and pulses briefly with
    // --accent outline"). Cleared once consumed.
    const [highlightApptId, setHighlightApptId] = useState<number | null>(urlApptId);
    // O1 (AB#2199, SO-09) — the deep-linked booking whose detail opens ONCE on
    // arrival (independent of the 2.5 s highlight, so a slow grid load still opens
    // it); cleared by the grid as soon as it opened it.
    const [focusApptId, setFocusApptId] = useState<number | null>(urlApptId);
    const [view, setView] = useState<CalendarView>(() => {
        try {
            const stored = window.localStorage.getItem(LAST_VIEW_KEY);
            return stored === 'week' ? 'week' : 'day';
        } catch { return 'day'; }
    });

    // Persist view choice — founder Q3 decision: localStorage last-choice.
    useEffect(() => {
        try { window.localStorage.setItem(LAST_VIEW_KEY, view); } catch { /* ignore */ }
    }, [view]);

    // Business name for the header subtitle (mockup §A — "Calendar Hilltop Salon").
    const { data: businessDetails } = useBusinessDetailsQuery();
    const businessName = businessDetails?.name ?? '';

    // AB#1752 — staff-first quick-book (mockup §D). Two related pieces of
    // state: the context-menu popover (right-click on staff header) + the
    // locked-staff Quick Book modal it can launch.
    const [staffMenu, setStaffMenu] = useState<{
        x: number;
        y: number;
        staffId: number;
        staffName: string;
        staffGender?: string | null;
    } | null>(null);
    const [lockedStaffBooking, setLockedStaffBooking] = useState<{
        staffId: number;
        staffName: string;
        staffGender?: string | null;
        /** Click-to-create — when set, the modal opens prefilled and skips the slot picker. */
        prefillDate?: string;
        prefillTime?: string;
    } | null>(null);
    // AB#1976 — quick add-break editor launched from the staff dropdown.
    const [absenceEditor, setAbsenceEditor] = useState<{
        staffId: number;
        staffName: string;
        /** AB#2021 — set to open the editor in edit mode for an existing break. */
        editAbsence?: StaffAbsenceDto;
    } | null>(null);

    // AB#2019 — read-only staff availability status panel.
    const [statusViewer, setStatusViewer] = useState<{
        staffId: number;
        staffName: string;
    } | null>(null);

    // AB#2023 — "who's free at" time check (null = off; drives per-column
    // free/blocked highlighting + the header availability pills).
    const [checkTime, setCheckTime] = useState<string | null>(null);

    // Branches for the picker. Same cache key as useBusinessSetupStatus so
    // the second consumer pays no extra network cost.
    const { data: branchData, isLoading: isBranchesLoading } = useQuery({
        queryKey: queryKeys.branches.list(),
        queryFn: () => apiService.getBranches(),
        staleTime: 60_000,
    });

    const activeBranches = useMemo(
        () => (branchData?.branches ?? []).filter(b => b.active),
        [branchData],
    );

    // Resolve the initial branch — URL `?branchId=` (deep link from List page) →
    // localStorage last choice → primary headquarters → first active branch.
    // Runs once branches load. The URL branch wins, and we strip the param
    // afterwards so a subsequent picker change isn't immediately overridden
    // by re-mounts that re-read the URL.
    useEffect(() => {
        if (branchId != null || activeBranches.length === 0) return;
        let fromUrl: number | null = null;
        if (!urlBranchConsumedRef.current && urlBranchId != null && activeBranches.some(b => b.id === urlBranchId)) {
            fromUrl = urlBranchId;
            urlBranchConsumedRef.current = true;
        }
        const stored = (() => {
            try {
                const raw = window.localStorage.getItem(LAST_BRANCH_KEY);
                const n = raw ? Number(raw) : NaN;
                return Number.isFinite(n) && activeBranches.some(b => b.id === n) ? n : null;
            } catch { return null; }
        })();
        const primary = activeBranches.find(b => b.primaryHeadQuarter)?.id;
        const fallback = activeBranches[0]?.id;
        setBranchId(fromUrl ?? stored ?? primary ?? fallback ?? null);
        // Strip the deep-link params once consumed so toggles + back-nav behave
        // normally. Keep `apptId` in state via `highlightApptId` for the
        // card-pulse effect.
        if (fromUrl != null || urlDay || urlApptId) {
            const next = new URLSearchParams(searchParams);
            next.delete('branchId');
            next.delete('apptId');
            next.delete('from');
            // Keep `day` because that's what already drives the date picker.
            setSearchParams(next, { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [branchId, activeBranches, urlBranchId]);

    // Clear the highlight after 2.5 s so re-navigations to the same card still
    // pulse, but the badge doesn't persist forever.
    useEffect(() => {
        if (highlightApptId == null) return;
        const id = window.setTimeout(() => setHighlightApptId(null), 2500);
        return () => window.clearTimeout(id);
    }, [highlightApptId]);

    // Persist the user's branch choice across visits.
    useEffect(() => {
        if (branchId == null) return;
        try { window.localStorage.setItem(LAST_BRANCH_KEY, String(branchId)); } catch { /* ignore */ }
    }, [branchId]);

    const {
        data: grid,
        isLoading: isGridLoading,
        isError,
        refetch,
    } = useCalendarGridQuery({ branchId, date });

    // Week-view data — 7 parallel day queries. Only fires when view='week'
    // because each day query is `enabled: branchId != null` and we
    // additionally only render the WeekView when `view === 'week'`.
    const weekStart = useMemo(() => weekStartIso(date), [date]);
    const weekResult = useCalendarWeekQuery({
        branchId: view === 'week' ? branchId : null,
        weekStart,
    });

    const selectedBranch = useMemo(() => {
        if (branchId == null) return null;
        return activeBranches.find(b => b.id === branchId) ?? null;
    }, [branchId, activeBranches]);
    const selectedBranchName = selectedBranch?.name ?? '';
    const selectedBranchIsHq = selectedBranch?.primaryHeadQuarter === true;
    // O3 (SO-23) — header identity: selected branch first; business name when no branch is selected.
    const headerIdentity = selectedBranchName || businessName;
    const headerSecondary = selectedBranchName
        && businessName
        && businessName.trim().toLowerCase() !== selectedBranchName.trim().toLowerCase()
        ? businessName
        : '';

    return (
        <div className={s.page}>
            <header className={s.header}>
                <div className={s.headerLeft}>
                    <h1 className={s.title}>
                        {t('cal.title')}
                        {/* O3 (SO-23) — the calendar is filtered to one branch, so the
                            branch is the primary identity; the business name follows
                            as secondary text only when it says something different. */}
                        {headerIdentity && (
                            <span className={s.titleBusiness}> {headerIdentity}</span>
                        )}
                        {headerSecondary && (
                            <span className={s.titleSecondary}> · {headerSecondary}</span>
                        )}
                    </h1>
                </div>
                <div className={s.headerRight}>
                    {/* View-mode tabs — Day / Week toggle (mockup §A + §B). */}
                    <div className={s.viewTabs} role="tablist" aria-label={t('cal.viewMode')}>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={view === 'day'}
                            className={`${s.viewTab} ${view === 'day' ? s.viewTabActive : ''}`}
                            onClick={() => setView('day')}
                        >
                            {t('cal.view.day')}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={view === 'week'}
                            className={`${s.viewTab} ${view === 'week' ? s.viewTabActive : ''}`}
                            onClick={() => setView('week')}
                        >
                            {t('cal.view.week')}
                        </button>
                    </div>

                    {/* Date controls — Day: ◂ "Wed 20 May" ▸. Week: ◂ "Sun 17 – Sat 23 May" ▸. */}
                    <div className={s.dateNav}>
                        <button
                            type="button"
                            className={s.navBtn}
                            aria-label={view === 'week' ? t('cal.prevWeek') : t('cal.prevDay')}
                            onClick={() => setDate(d => addDaysIso(d, view === 'week' ? -7 : -1))}
                        >
                            <span className={s.navGlyph} aria-hidden="true">‹</span>
                        </button>
                        <span className={s.dateLabel} aria-live="polite">
                            {view === 'week' ? formatWeekRange(weekStart, language) : formatDateShort(date, language)}
                        </span>
                        <button
                            type="button"
                            className={s.navBtn}
                            aria-label={view === 'week' ? t('cal.nextWeek') : t('cal.nextDay')}
                            onClick={() => setDate(d => addDaysIso(d, view === 'week' ? 7 : 1))}
                        >
                            <span className={s.navGlyph} aria-hidden="true">›</span>
                        </button>
                        <button
                            type="button"
                            className={s.todayBtn}
                            onClick={() => setDate(localTodayIso())}
                        >
                            {t('cal.today')}
                        </button>
                        <DateField
                            className={s.dateInput}
                            value={date}
                            onChange={iso => setDate(iso || localTodayIso())}
                            label={t('cal.pickDate')}
                        />
                    </div>

                    {/* Branch picker — hidden when single-branch.
                        HQ badge shown inline next to the picker when the selected
                        branch is the primary headquarters (mockup §A). */}
                    {activeBranches.length > 1 ? (
                        <div className={s.branchPickerWrap}>
                            <select
                                className={s.branchPicker}
                                value={branchId ?? ''}
                                onChange={e => setBranchId(Number(e.target.value))}
                                aria-label={t('cal.selectBranch')}
                            >
                                {activeBranches.map(b => (
                                    <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                            </select>
                            {selectedBranchIsHq && <span className={s.hqBadge}>{t('cal.hq')}</span>}
                        </div>
                    ) : activeBranches.length === 1 ? (
                        <div className={s.branchPickerWrap}>
                            <span className={s.branchLabel}>{selectedBranchName}</span>
                            {selectedBranchIsHq && <span className={s.hqBadge}>{t('cal.hq')}</span>}
                        </div>
                    ) : null}

                    {/* AB#2023 — "who's free at" check. Pick the caller's time
                        and the grid highlights which staff can take it. */}
                    {view === 'day' && branchId != null && (
                        <div className={s.availCheck}>
                            <span className={s.availCheckLabel}>{t('calendar.avail.checkLabel')}</span>
                            <input
                                type="time"
                                className={s.availCheckInput}
                                value={checkTime ?? ''}
                                onChange={(e) => setCheckTime(e.target.value || null)}
                                aria-label={t('calendar.avail.checkLabel')}
                            />
                            {checkTime && (
                                <button
                                    type="button"
                                    className={s.availCheckClear}
                                    onClick={() => setCheckTime(null)}
                                >
                                    {t('calendar.avail.checkClear')}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Quick book launcher (mockup §A) — opens the same 3-step
                        modal as the one on /appointments. Reuses the existing
                        component so the entry-point is consistent. */}
                    {branchId != null && (
                        <QuickBookLauncher
                            branchId={branchId}
                            branchName={selectedBranchName}
                            label={t('cal.quickBook')}
                        />
                    )}
                </div>
            </header>

            {/* Body — Day or Week view */}
            {view === 'day' ? (
                <>
                    {(isBranchesLoading || isGridLoading || branchId == null) && (
                        <CalendarSkeleton />
                    )}

                    {isError && !isGridLoading && (
                        <div className={s.errorCard}>
                            {t('cal.loadFailed')}
                            <div>
                                <button
                                    type="button"
                                    className={s.retryBtn}
                                    onClick={() => refetch()}
                                >
                                    {t('cal.retry')}
                                </button>
                            </div>
                        </div>
                    )}

                    {grid && !isGridLoading && !isError && (
                        grid.isClosed ? (
                            <div className={s.placard}>
                                <div className={s.placardEmoji} aria-hidden="true">📅</div>
                                <h2 className={s.placardTitle}>{t('cal.closed.title')}</h2>
                                <p className={s.placardSub}>
                                    {t('cal.closed.body').replace('{date}', formatDateLong(date, language))}
                                </p>
                            </div>
                        ) : (
                            <CalendarGrid
                                data={grid}
                                viewedDate={date}
                                checkTime={checkTime}
                                branchName={selectedBranchName || null}
                                focusAppointmentId={focusApptId}
                                onFocusConsumed={() => setFocusApptId(null)}
                                highlightAppointmentId={highlightApptId}
                                onStaffHeaderContextMenu={(e, st) => {
                                    setStaffMenu({
                                        x: e.clientX,
                                        y: e.clientY,
                                        staffId: st.staffId,
                                        staffName: st.displayName,
                                        staffGender: st.gender,
                                    });
                                }}
                                onEmptyCellClick={(st, clickedDate, clickedTime) => {
                                    // Click-to-create: open the locked-staff
                                    // modal prefilled with the exact (staff, date, time)
                                    // the owner clicked. Skips the slot picker.
                                    setLockedStaffBooking({
                                        staffId: st.staffId,
                                        staffName: st.displayName,
                                        staffGender: st.gender,
                                        prefillDate: clickedDate,
                                        prefillTime: clickedTime,
                                    });
                                }}
                            />
                        )
                    )}
                </>
            ) : (
                /* Week view — 7-day compact grid (mockup §B). Loading when
                   branch isn't resolved yet OR when every day is still
                   fetching its first response; shows partial week as
                   data streams in. */
                branchId == null || isBranchesLoading ? (
                    <CalendarSkeleton />
                ) : (
                    <WeekView
                        days={weekResult.days}
                        todayIso={localTodayIso()}
                        onEmptyCellClick={(clickedDate, clickedTime) => {
                            // Week view has no per-staff column — auto-pick
                            // a target staff so the modal's prefill mode can
                            // engage. B25a refined the heuristic: prefer a
                            // staff whose shift COVERS the clicked time so
                            // BE doesn't reject with Slot.OUTSIDE_SHIFT after
                            // the owner walks through the whole flow. Today
                            // the only requirement was "shift start + end
                            // set at all" — that lands on staff who work
                            // 14:00-22:00 even when the click is at 10:00.
                            //
                            // G6 (audit Phase A-2) — Before opening the
                            // modal we sweep the day's staff list and check
                            // whether ANY staff covers clickedTime. If
                            // nobody does, fire a toast warning so the
                            // owner knows upfront they're going to land in
                            // the OUTSIDE_SHIFT empty-state. The modal
                            // still opens (override path: maybe they want
                            // to extend a shift, or just see the picker)
                            // — but the heads-up saves the round-trip
                            // through 3 modal steps for the common case.
                            //
                            // Three-tier fallback:
                            //   1. Staff whose shift covers clickedTime
                            //   2. Any staff with a shift on the day
                            //      (BE may still reject — drops to picker)
                            //   3. First staff at the branch (off-day override)
                            // BE always re-validates; if every tier lands on
                            // a staff who can't take it, the modal drops to
                            // the slot picker with the staff lock released
                            // (B25b) so the owner sees alternates at the
                            // clicked time across all on-shift staff.
                            const dayGrid = weekResult.days.find(d => d.date === clickedDate)?.grid;
                            const coversClickedTime = (
                                s2: { shiftStart?: string | null; shiftEnd?: string | null }
                            ) => !!(
                                s2.shiftStart && s2.shiftEnd
                                && s2.shiftStart <= clickedTime
                                && clickedTime < s2.shiftEnd
                            );
                            const onShiftCovering = dayGrid?.staff.find(coversClickedTime);
                            const anyWorking = dayGrid?.staff.find(
                                s2 => s2.shiftStart && s2.shiftEnd,
                            );
                            const fallback = dayGrid?.staff[0];
                            const target = onShiftCovering ?? anyWorking ?? fallback;
                            if (!target) {
                                // No staff at the branch at all — opening the
                                // modal would be pointless; no-op.
                                return;
                            }
                            // G6 — Early warning when nobody covers clickedTime.
                            // The modal still opens (so the user can override),
                            // but they see the issue before clicking through.
                            if (!onShiftCovering) {
                                const niceDate = formatDateShort(clickedDate, language);
                                showToast(
                                    t('cal.toast.nobodyOnShift').replace('{time}', clickedTime).replace('{date}', niceDate),
                                    'info',
                                );
                            }
                            setLockedStaffBooking({
                                staffId: target.staffId,
                                staffName: target.displayName,
                                staffGender: target.gender,
                                prefillDate: clickedDate,
                                prefillTime: clickedTime,
                            });
                        }}
                    />
                )
            )}

            {/* AB#1752 — staff-first quick-book (mockup §D). Right-click on a
                staff column header opens this menu; "Book with {name}…" hands
                off to the locked-staff QuickBookModal below. */}
            {staffMenu && (
                <StaffContextMenu
                    x={staffMenu.x}
                    y={staffMenu.y}
                    staffId={staffMenu.staffId}
                    staffName={staffMenu.staffName}
                    onBookWith={() => {
                        setLockedStaffBooking({
                            staffId: staffMenu.staffId,
                            staffName: staffMenu.staffName,
                            staffGender: staffMenu.staffGender,
                        });
                    }}
                    onAddBreak={() => {
                        setAbsenceEditor({
                            staffId: staffMenu.staffId,
                            staffName: staffMenu.staffName,
                        });
                    }}
                    onViewStatus={() => {
                        setStatusViewer({
                            staffId: staffMenu.staffId,
                            staffName: staffMenu.staffName,
                        });
                    }}
                    onClose={() => setStaffMenu(null)}
                />
            )}

            {/* AB#2019 — staff availability status panel; AB#2021 — edit/remove today's breaks. */}
            {statusViewer && branchId != null && (
                <StaffStatusModal
                    staffId={statusViewer.staffId}
                    staffName={statusViewer.staffName}
                    branchId={branchId}
                    onEditBreak={(absence) => {
                        // Hand the break to the editor in edit mode; close the panel.
                        setAbsenceEditor({
                            staffId: statusViewer.staffId,
                            staffName: statusViewer.staffName,
                            editAbsence: absence,
                        });
                        setStatusViewer(null);
                    }}
                    onClose={() => setStatusViewer(null)}
                />
            )}

            {/* AB#1976 — quick add-break editor, prefilled with the staff from
                the dropdown + the day currently shown on the calendar. */}
            {absenceEditor && branchId != null && (
                <QuickAbsenceModal
                    staffId={absenceEditor.staffId}
                    staffName={absenceEditor.staffName}
                    branchId={branchId}
                    date={date}
                    editAbsence={absenceEditor.editAbsence}
                    onClose={() => setAbsenceEditor(null)}
                    onSaved={() => {
                        const editedStaffId = absenceEditor.staffId;
                        setAbsenceEditor(null);
                        // AB#2015 — refetch the grid so the new/edited break/leave
                        // block appears immediately on the calendar.
                        queryClient.invalidateQueries({
                            queryKey: queryKeys.calendar.grid({ branchId: branchId ?? 0, date }),
                        });
                        // AB#2021 — refresh the availability panel's absence cache too.
                        queryClient.invalidateQueries({ queryKey: ['staff-absences', editedStaffId] });
                    }}
                />
            )}

            {lockedStaffBooking && branchId != null && (
                <QuickBookModal
                    branchId={branchId}
                    branchName={selectedBranchName}
                    lockedStaffId={lockedStaffBooking.staffId}
                    lockedStaffName={lockedStaffBooking.staffName}
                    lockedStaffGender={
                        lockedStaffBooking.staffGender === 'Female' ? 'Female'
                        : lockedStaffBooking.staffGender === 'Male' ? 'Male'
                        : null
                    }
                    prefillDate={lockedStaffBooking.prefillDate}
                    prefillTime={lockedStaffBooking.prefillTime}
                    onClose={() => setLockedStaffBooking(null)}
                    onBooked={() => {
                        // Click-to-create + booking-confirmed: collapse the
                        // modal state so opening another empty cell starts
                        // a fresh modal next time.
                        setLockedStaffBooking(null);
                    }}
                />
            )}
        </div>
    );
};

export default CalendarPage;
